import { Bell, CheckCheck, X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Offcanvas, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import "../css/NotificationPanel.css";
import { logger } from "../utils/logger";

const NotificationPanel = () => {
	const [show, setShow] = useState(false);
	const [notifications, setNotifications] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const navigate = useNavigate();

	const fetchNotifications = useCallback(async (silent = false) => {
		if (!silent) {
			setLoading(true);
		}

		try {
			const response = await api.get("/notifications/");
			setNotifications(response.data);
			setError("");
		} catch (fetchError) {
			logger.error("Unable to load in-app notifications", {
				url: "/notifications/",
				error: fetchError,
			});
			setError("Notifications could not be loaded.");
		} finally {
			if (!silent) {
				setLoading(false);
			}
		}
	}, []);

	useEffect(() => {
		fetchNotifications();

		const intervalId = window.setInterval(() => {
			fetchNotifications(true);
		}, 60 * 60 * 1000);

		const handleFocus = () => {
			fetchNotifications(true);
		};

		window.addEventListener("focus", handleFocus);

		return () => {
			window.clearInterval(intervalId);
			window.removeEventListener("focus", handleFocus);
		};
	}, [fetchNotifications]);

	const unreadCount = useMemo(
		() => notifications.filter((notification) => !notification.is_read).length,
		[notifications]
	);

	const handleOpen = () => {
		setShow(true);
		fetchNotifications(true);
	};

	const markAsRead = async (notification) => {
		if (notification.is_read) {
			return;
		}

		try {
			const response = await api.post(`/notifications/${notification.id}/read/`);
			setNotifications((current) =>
				current.map((item) =>
					item.id === notification.id ? response.data : item
				)
			);
		} catch (readError) {
			logger.error("Unable to mark notification as read", {
				notificationId: notification.id,
				error: readError,
			});
		}
	};

	const handleNotificationClick = async (notification) => {
		await markAsRead(notification);

		if (notification.action_url) {
			setShow(false);
			navigate(notification.action_url);
		}
	};

	const handleMarkAllRead = async () => {
		try {
			await api.post("/notifications/read-all/");
			setNotifications((current) =>
				current.map((notification) => ({
					...notification,
					is_read: true,
					read_at: notification.read_at || new Date().toISOString(),
				}))
			);
		} catch (readError) {
			logger.error("Unable to mark all notifications as read", {
				url: "/notifications/read-all/",
				error: readError,
			});
		}
	};

	const handleDismiss = async (event, notification) => {
		event.stopPropagation();

		try {
			await api.post(`/notifications/${notification.id}/dismiss/`);
			setNotifications((current) =>
				current.filter((item) => item.id !== notification.id)
			);
		} catch (dismissError) {
			logger.error("Unable to dismiss notification", {
				notificationId: notification.id,
				error: dismissError,
			});
		}
	};

	const formatCreatedAt = (createdAt) => {
		return new Intl.DateTimeFormat("en-AU", {
			day: "numeric",
			month: "short",
			year: "numeric",
			hour: "numeric",
			minute: "2-digit",
		}).format(new Date(createdAt));
	};

	return (
		<>
			<button
				type="button"
				className="notification-bell-button"
				onClick={handleOpen}
				aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : "Notifications"}
				title="Notifications"
			>
				<Bell size={28} />
				{unreadCount > 0 && (
					<span className="notification-bell-badge">
						{unreadCount > 99 ? "99+" : unreadCount}
					</span>
				)}
			</button>

			<Offcanvas
				show={show}
				onHide={() => setShow(false)}
				placement="end"
				className="notification-panel"
			>
				<Offcanvas.Header closeButton>
					<div className="notification-panel-header">
						<Offcanvas.Title>Notifications</Offcanvas.Title>
						{unreadCount > 0 && (
							<button
								type="button"
								className="notification-mark-all-button"
								onClick={handleMarkAllRead}
							>
								<CheckCheck size={17} />
								Mark all read
							</button>
						)}
					</div>
				</Offcanvas.Header>
				<Offcanvas.Body>
					{loading && (
						<div className="notification-panel-state">
							<Spinner animation="border" size="sm" />
							<span>Loading notifications...</span>
						</div>
					)}

					{!loading && error && (
						<div className="notification-panel-state notification-panel-error">
							{error}
						</div>
					)}

					{!loading && !error && notifications.length === 0 && (
						<div className="notification-panel-empty">
							<Bell size={30} />
							<strong>You're all caught up</strong>
							<span>There are no notifications to review.</span>
						</div>
					)}

					{!loading && !error && notifications.length > 0 && (
						<div className="notification-list">
							{notifications.map((notification) => (
								<div
									key={notification.id}
									className={`notification-item${notification.is_read ? "" : " notification-item-unread"}`}
									role="button"
									tabIndex={0}
									onClick={() => handleNotificationClick(notification)}
									onKeyDown={(event) => {
										if (event.key === "Enter" || event.key === " ") {
											event.preventDefault();
											handleNotificationClick(notification);
										}
									}}
								>
									<div className="notification-item-indicator" />
									<div className="notification-item-content">
										<div className="notification-item-title">{notification.title}</div>
										<div className="notification-item-message">{notification.message}</div>
										<div className="notification-item-date">
											{formatCreatedAt(notification.created_at)}
										</div>
									</div>
									<button
										type="button"
										className="notification-dismiss-button"
										onClick={(event) => handleDismiss(event, notification)}
										onKeyDown={(event) => event.stopPropagation()}
										aria-label="Clear notification"
										title="Clear notification"
									>
										<X size={18} />
									</button>
								</div>
							))}
						</div>
					)}
				</Offcanvas.Body>
			</Offcanvas>
		</>
	);
};

export default NotificationPanel;
